import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Bell, Send, ClipboardList, FileText, CreditCard, MessageSquare,
  CheckCircle, AlertTriangle, Clock, RefreshCw, ChevronDown, ChevronUp, History
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const RelanceCard = ({ title, icon: Icon, color, count, items, onSend, sending, renderItem }) => {
  const [expanded, setExpanded] = useState(false);
  const colorMap = {
    amber: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', icon: 'text-amber-500', badge: 'bg-amber-500' },
    blue: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', icon: 'text-blue-500', badge: 'bg-blue-500' },
    red: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', icon: 'text-red-500', badge: 'bg-red-500' },
    orange: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', icon: 'text-orange-500', badge: 'bg-orange-500' },
  };
  const c = colorMap[color] || colorMap.amber;

  return (
    <div className={`${c.bg} border ${c.border} rounded-lg overflow-hidden`} data-testid={`relance-card-${color}`}>
      <div className="p-4 flex items-center justify-between cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center gap-3">
          <Icon className={`h-5 w-5 ${c.icon}`} />
          <h3 className={`font-semibold ${c.text}`}>{title}</h3>
          <Badge className={`${c.badge} text-white`}>{count}</Badge>
        </div>
        <div className="flex items-center gap-2">
          {count > 0 && (
            <Button
              size="sm"
              className="bg-[#0F172A] hover:bg-[#1e293b] text-white"
              onClick={(e) => { e.stopPropagation(); onSend(); }}
              disabled={sending}
              data-testid={`btn-send-${color}`}
            >
              {sending ? <RefreshCw className="h-3 w-3 mr-1 animate-spin" /> : <Send className="h-3 w-3 mr-1" />}
              Envoyer
            </Button>
          )}
          {count > 0 ? (expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />) : <CheckCircle className="h-4 w-4 text-green-500" />}
        </div>
      </div>
      {expanded && items.length > 0 && (
        <div className="px-4 pb-4 space-y-2">
          {items.map((item, i) => (
            <div key={i} className="bg-white rounded-md p-3 text-sm border border-slate-100">
              {renderItem(item)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const RelancesPage = () => {
  const [status, setStatus] = useState(null);
  const [historique, setHistorique] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState({});
  const [showHistorique, setShowHistorique] = useState(false);

  const loadData = async () => {
    try {
      const [statusRes, histRes] = await Promise.all([
        axios.get('/relances/status'),
        axios.get('/relances/historique')
      ]);
      setStatus(statusRes.data);
      setHistorique(histRes.data);
    } catch (e) {
    }
    setLoading(false);
  };

  useEffect(() => { loadData(); }, []);

  const handleSend = async (type) => {
    setSending(prev => ({ ...prev, [type]: true }));
    try {
      const res = await axios.post(`/relances/envoyer/${type}`);
      toast.success(res.data.message);
      if (res.data.errors?.length > 0) {
        toast.error(`${res.data.errors.length} erreur(s) d'envoi`);
      }
      await loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'envoi");
    }
    setSending(prev => ({ ...prev, [type]: false }));
  };

  const handleSendAll = async () => {
    setSending(prev => ({ ...prev, all: true }));
    try {
      const res = await axios.post('/relances/envoyer/toutes');
      toast.success(res.data.message);
      await loadData();
    } catch (e) {
      toast.error("Erreur lors de l'envoi");
    }
    setSending(prev => ({ ...prev, all: false }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="h-8 w-8 text-slate-400 animate-spin" />
      </div>
    );
  }

  const totalRelances = status?.total_relances || 0;

  return (
    <div className="space-y-6" data-testid="relances-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-4xl font-display font-bold text-[#0F172A] mb-2">Relances Automatiques</h1>
          <p className="text-slate-600">Envoyez des rappels par email aux tuteurs, entreprises et stagiaires</p>
        </div>
        <div className="flex items-center gap-3">
          {totalRelances > 0 && (
            <Button
              className="bg-[#8DC63F] hover:bg-[#7ab535] text-white"
              onClick={handleSendAll}
              disabled={sending.all}
              data-testid="btn-send-all"
            >
              {sending.all ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              Tout envoyer ({totalRelances})
            </Button>
          )}
          <Button variant="outline" onClick={loadData} data-testid="btn-refresh">
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualiser
          </Button>
        </div>
      </div>

      {/* Summary */}
      {totalRelances === 0 ? (
        <div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center" data-testid="no-relances">
          <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
          <h2 className="text-xl font-semibold text-green-800 mb-1">Tout est a jour !</h2>
          <p className="text-green-600">Aucune relance a envoyer pour le moment</p>
        </div>
      ) : (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-500" />
          <p className="text-amber-800 font-medium">{totalRelances} relance(s) en attente d'envoi</p>
        </div>
      )}

      {/* Relance Cards */}
      <div className="space-y-4">
        <RelanceCard
          title="Grilles d'evaluation non remplies"
          icon={ClipboardList}
          color="amber"
          count={status?.grilles_vides?.count || 0}
          items={status?.grilles_vides?.items || []}
          onSend={() => handleSend('grilles')}
          sending={sending.grilles}
          renderItem={(item) => (
            <div className="flex items-center justify-between">
              <div>
                <span className="font-medium">{item.stagiaire_nom}</span>
                <span className="text-slate-400 mx-2">-</span>
                <span className="text-slate-500">Tuteur: {item.tuteur_nom}</span>
              </div>
              <span className="text-xs text-slate-400">{item.tuteur_email}</span>
            </div>
          )}
        />

        <RelanceCard
          title="Bilans mensuels en retard"
          icon={MessageSquare}
          color="blue"
          count={status?.bilans_retard?.count || 0}
          items={status?.bilans_retard?.items || []}
          onSend={() => handleSend('bilans')}
          sending={sending.bilans}
          renderItem={(item) => (
            <div className="flex items-center justify-between">
              <div>
                <span className="font-medium">{item.entreprise_nom}</span>
                <span className="text-slate-400 mx-2">-</span>
                <span className="text-blue-600 capitalize">{item.mois_manquant} {item.annee}</span>
              </div>
              <span className="text-xs text-slate-400">{item.email}</span>
            </div>
          )}
        />

        <RelanceCard
          title="Documents manquants"
          icon={FileText}
          color="orange"
          count={status?.docs_manquants?.count || 0}
          items={status?.docs_manquants?.items || []}
          onSend={() => handleSend('documents')}
          sending={sending.documents}
          renderItem={(item) => (
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="font-medium">{item.stagiaire_nom}</span>
                <span className="text-xs text-slate-400">{item.email}</span>
              </div>
              <div className="flex flex-wrap gap-1">
                {item.docs_manquants.map((d, i) => (
                  <Badge key={i} variant="outline" className="text-xs text-orange-600 border-orange-300">{d}</Badge>
                ))}
              </div>
            </div>
          )}
        />

        <RelanceCard
          title="Paiements en retard"
          icon={CreditCard}
          color="red"
          count={status?.paiements_retard?.count || 0}
          items={status?.paiements_retard?.items || []}
          onSend={() => handleSend('paiements')}
          sending={sending.paiements}
          renderItem={(item) => (
            <div className="flex items-center justify-between">
              <div>
                <span className="font-medium">{item.prospect_nom}</span>
                <span className="text-slate-400 mx-2">-</span>
                <span className="text-slate-500">Facture {item.facture_numero}</span>
              </div>
              <span className="font-bold text-red-600">{item.montant_total_retard?.toLocaleString('fr-FR')} EUR</span>
            </div>
          )}
        />
      </div>

      {/* Historique */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm">
        <div
          className="p-4 flex items-center justify-between cursor-pointer"
          onClick={() => setShowHistorique(!showHistorique)}
          data-testid="historique-toggle"
        >
          <div className="flex items-center gap-2">
            <History className="h-5 w-5 text-slate-500" />
            <h3 className="font-semibold text-slate-800">Historique des relances</h3>
            <Badge variant="outline">{historique.length}</Badge>
          </div>
          {showHistorique ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </div>
        {showHistorique && (
          <div className="px-4 pb-4">
            {historique.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">Aucune relance envoyee</p>
            ) : (
              <div className="space-y-2">
                {historique.map((h, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-md text-sm">
                    <div className="flex items-center gap-3">
                      <Clock className="h-4 w-4 text-slate-400" />
                      <span className="text-slate-500">{new Date(h.date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                      <Badge variant="outline" className="capitalize">{h.type}</Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-green-600 font-medium">{h.sent} envoyee(s)</span>
                      {h.errors > 0 && <span className="text-red-500">{h.errors} erreur(s)</span>}
                      <span className="text-xs text-slate-400">par {h.envoye_par}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RelancesPage;
